/**
 * DesktopAgentModule.jsx
 * Standalone Time Tracking module — can be sold/deployed independently.
 *
 * Views:
 *   dashboard  — Live team overview, today's stats, top performers
 *   trackers   — All tracked users list + Add User
 *   detail     — Individual deep-dive (Daily / Weekly / Monthly / Custom)
 *   reports    — Period reports + CSV export
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Monitor, Users, Clock, TrendingUp, Wifi, WifiOff,
  Coffee, Search, Plus, Download, RefreshCw, ChevronLeft,
  Calendar, BarChart2, FileText, AlertCircle, CheckCircle,
  X, Mail, Activity, Zap, Eye, ArrowUp, ArrowDown,
} from "lucide-react";
// No recharts dependency — using CSS-based charts

import { apiBaseUrl } from "services/config";
import { APIConstant } from "services/api-constant";
import { useTheme } from "context/ThemeContext";

const API = apiBaseUrl;

// ── Auth helper — reads platform_token (same key as entire admin portal) ──
// Previous version tried "access_token" first which is always empty → 401
const ax = () => ({
  headers: {
    Authorization: `Bearer ${sessionStorage.getItem("platform_token") || ""}`,
  },
});

// ── Design tokens now come from ThemeContext (useTheme) ─────────
function useStatusCfg() {
  const {T} = useTheme();
  return {
    active:  { color: T.green,  label: "Active",  icon: <Wifi size={11}/> },
    idle:    { color: T.amber,  label: "Idle",    icon: <Coffee size={11}/> },
    offline: { color: T.dim,    label: "Offline", icon: <WifiOff size={11}/> },
  };
}

// ── Formatters ─────────────────────────────────────────────────
const fmtHM  = (s) => { const h = Math.floor((s||0)/3600), m = Math.floor(((s||0)%3600)/60); return `${h}h ${m}m`; };
const fmtH   = (s) => (((s||0)/3600).toFixed(1)) + "h";
const fmtPct = (n) => `${Math.round(n||0)}%`;
const fmtDate= (iso) => { if(!iso) return "—"; const d=new Date(iso); return d.toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"}); };
const initials= (f="",l="") => `${f[0]||""}${l[0]||""}`.toUpperCase();
const COLORS  = ["#00C8FF","#7C5CFF","#00E5A3","#FFB800","#FF4D6D","#3B82F6","#EC4899","#F59E0B"];

const APP_ICONS = {
  "Google Chrome":"🌐","Chrome":"🌐","Safari":"🧭","Firefox":"🦊",
  "Visual Studio Code":"💻","VS Code":"💻","Code":"💻",
  "Terminal":"⬛","iTerm2":"⬛","cmd.exe":"⬛","PowerShell":"⬛",
  "Slack":"💬","Microsoft Teams":"💬","Zoom":"📹","Google Meet":"📹",
  "Figma":"🎨","Sketch":"🎨","Photoshop":"🖼",
  "Excel":"📊","Numbers":"📊","Word":"📝","Notion":"📝",
  "Spotify":"🎵","YouTube":"▶️","Mail":"📧","Outlook":"📧",
  "Postman":"📬","Xcode":"🔨","Android Studio":"🤖",
};
const appIcon = (name) => {
  const k = Object.keys(APP_ICONS).find(k => name?.toLowerCase().includes(k.toLowerCase()));
  return k ? APP_ICONS[k] : "🖥";
};

const PRODUCTIVE_APPS = ["code","vscode","visual studio","xcode","figma","sketch",
  "postman","android studio","terminal","iterm","intellij","webstorm","pycharm"];
const isProductive = (app="") => PRODUCTIVE_APPS.some(k => app.toLowerCase().includes(k));

// ── Shared UI pieces ───────────────────────────────────────────
const Card = ({children, style={}}) => {
  const {T} = useTheme();
  return (
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,...style}}>
      {children}
    </div>
  );
};

const StatCard = ({label,value,sub,color,icon,trend}) => {
  const {T} = useTheme();
  return (
    <Card style={{padding:"18px 20px",flex:1,minWidth:140}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
        <div style={{fontSize:10,color:T.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em"}}>{label}</div>
        {icon && <div style={{color:T[color]||color,opacity:0.6}}>{icon}</div>}
      </div>
      <div style={{fontSize:26,fontWeight:900,color:T[color]||color||T.cyan,fontFamily:"JetBrains Mono,monospace"}}>{value}</div>
      {sub && <div style={{fontSize:11,color:T.dim,marginTop:4}}>{sub}</div>}
      {trend != null && (
        <div style={{fontSize:11,color:trend>=0?T.green:T.red,marginTop:4,display:"flex",alignItems:"center",gap:3}}>
          {trend>=0?<ArrowUp size={10}/>:<ArrowDown size={10}/>} {Math.abs(trend)}% vs yesterday
        </div>
      )}
    </Card>
  );
};

const StatusDot = ({status,size=8}) => {
  const {T} = useTheme();
  const cfg = useStatusCfg();
  return (
    <span style={{
      display:"inline-block",width:size,height:size,borderRadius:"50%",
      background:cfg[status]?.color||T.dim,flexShrink:0,
      boxShadow:status==="active"?`0 0 6px ${T.green}`:undefined,
      animation:status==="active"?"pulse 1.5s infinite":undefined,
    }}/>
  );
};

const Btn = ({onClick,disabled,variant="primary",size="md",children,style={}}) => {
  const {T} = useTheme();
  const pad = size==="sm"?"5px 12px":"9px 20px";
  const bg  = variant==="primary"
    ? `linear-gradient(135deg,${T.cyan},${T.purple})`
    : variant==="ghost"
      ? T.toggle_bg
      : `${variant==="danger"?T.red:T.green}1A`;
  const col = variant==="primary" ? "#fff" : variant==="danger" ? T.red : T[variant]||T.muted;
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding:pad,borderRadius:8,border:variant==="primary"?"none":`1px solid ${col}30`,
      background:bg,color:col,cursor:disabled?"not-allowed":"pointer",opacity:disabled?0.6:1,
      fontFamily:"Outfit,sans-serif",fontSize:size==="sm"?12:13,fontWeight:700,
      display:"inline-flex",alignItems:"center",gap:6,...style
    }}>{children}</button>
  );
};

function useInp() {
  const {T} = useTheme();
  return {
    background:T.inp_bg,border:`1px solid ${T.border}`,borderRadius:8,
    color:T.text,fontSize:13,padding:"9px 12px",outline:"none",
    fontFamily:"Outfit,sans-serif",boxSizing:"border-box",width:"100%",
  };
}
function useLbl() {
  const {T} = useTheme();
  return {fontSize:11,color:T.muted,display:"block",marginBottom:5,
    textTransform:"uppercase",letterSpacing:"0.07em",fontWeight:700};
}

// ══════════════════════════════════════════════════════════════
// DASHBOARD VIEW
// ══════════════════════════════════════════════════════════════
function DashboardView({employees, presence}) {
  const {T} = useTheme();
  const empMap = useMemo(() => {
    const m = {};
    employees.forEach(e => { m[e.id] = e; });
    return m;
  }, [employees]);

  const activeCount  = Object.values(presence).filter(p=>p.status==="active").length;
  const idleCount    = Object.values(presence).filter(p=>p.status==="idle").length;
  const offlineCount = employees.length - activeCount - idleCount;
  const totalToday   = Object.values(presence).reduce((a,p)=>a+(p.today_active_seconds||0),0);
  const avgToday     = employees.length ? Math.round(totalToday/employees.length) : 0;

  // Top apps across team
  const appTotals = {};
  Object.values(presence).forEach(p => {
    if(p.active_app) appTotals[p.active_app] = (appTotals[p.active_app]||0)+1;
  });
  const topApps = Object.entries(appTotals).sort((a,b)=>b[1]-a[1]).slice(0,5);

  // Per-employee bar chart data
  const chartData = employees
    .filter(e => presence[e.id])
    .map(e => ({
      name: `${e.first_name}`,
      hours: parseFloat(fmtH(presence[e.id]?.today_active_seconds||0)),
      status: presence[e.id]?.status || "offline",
    }))
    .sort((a,b) => b.hours - a.hours)
    .slice(0,10);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      {/* Stats row */}
      <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
        <StatCard label="Online Now"    value={activeCount}     color="green"  icon={<Wifi size={16}/>}       sub={`of ${employees.length} tracked`}/>
        <StatCard label="Idle"          value={idleCount}       color="amber"  icon={<Coffee size={16}/>}     sub="No activity > 1 min"/>
        <StatCard label="Offline"       value={offlineCount}    color="dim"    icon={<WifiOff size={16}/>}    sub="Agent not running"/>
        <StatCard label="Avg Today"     value={fmtHM(avgToday)} color="cyan"   icon={<Clock size={16}/>}      sub="Active time per person"/>
        <StatCard label="Total Hours"   value={fmtHM(totalToday)} color="purple" icon={<TrendingUp size={16}/>} sub="Team combined today"/>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 340px",gap:16}}>
        {/* Today's hours — CSS bar chart */}
        <Card style={{padding:"18px 20px"}}>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:14}}>
            Today's Active Hours — Team
          </div>
          {chartData.length > 0 ? (
            <div style={{display:"flex",flexDirection:"column",gap:8,maxHeight:220,overflowY:"auto"}}>
              {chartData.map((d,i)=>{
                const barColor = d.status==="active"?T.green:d.status==="idle"?T.amber:T.dim;
                const maxHours = Math.max(...chartData.map(x=>x.hours),8);
                const pct = Math.round((d.hours/maxHours)*100);
                return (
                  <div key={i} style={{display:"flex",alignItems:"center",gap:10}}>
                    <div style={{width:70,fontSize:11,color:T.muted,textAlign:"right",flexShrink:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {d.name}
                    </div>
                    <div style={{flex:1,height:20,background:T.toggle_bg,borderRadius:4,overflow:"hidden"}}>
                      <div style={{width:`${pct}%`,height:"100%",background:barColor,borderRadius:4,
                        transition:"width 0.5s",minWidth:d.hours>0?4:0}}/>
                    </div>
                    <div style={{width:36,fontSize:11,fontFamily:"JetBrains Mono,monospace",
                      color:barColor,fontWeight:700,flexShrink:0}}>
                      {d.hours>0?`${d.hours}h`:"—"}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{height:140,display:"flex",alignItems:"center",justifyContent:"center",color:T.dim,fontSize:13}}>
              No data for today
            </div>
          )}
          <div style={{display:"flex",gap:16,marginTop:12}}>
            {[["Active",T.green],["Idle",T.amber],["Offline",T.dim]].map(([l,c])=>(
              <div key={l} style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:T.muted}}>
                <span style={{width:8,height:8,borderRadius:2,background:c,display:"inline-block"}}/>
                {l}
              </div>
            ))}
          </div>
        </Card>

        {/* Right column */}
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          {/* Top apps */}
          <Card style={{padding:"16px 18px"}}>
            <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:12}}>
              🖥 Top Apps Right Now
            </div>
            {topApps.length > 0 ? topApps.map(([app,count],i)=>(
              <div key={app} style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:16}}>{appIcon(app)}</span>
                  <span style={{fontSize:12,color:T.muted,maxWidth:140,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{app}</span>
                </div>
                <span style={{fontSize:11,fontWeight:700,color:COLORS[i%COLORS.length],fontFamily:"JetBrains Mono,monospace"}}>
                  {count} user{count!==1?"s":""}
                </span>
              </div>
            )) : (
              <div style={{fontSize:12,color:T.dim,textAlign:"center",padding:"12px 0"}}>No active sessions</div>
            )}
          </Card>

          {/* Quick status */}
          <Card style={{padding:"16px 18px"}}>
            <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:12}}>⚡ Live Status</div>
            <div style={{display:"flex",flexDirection:"column",gap:7,maxHeight:180,overflowY:"auto"}}>
              {employees.map(emp => {
                const p = presence[emp.id];
                const st= p?.status||"offline";
                return (
                  <div key={emp.id} style={{display:"flex",alignItems:"center",gap:8}}>
                    <StatusDot status={st}/>
                    <span style={{fontSize:12,color:st==="offline"?T.dim:T.muted,flex:1}}>
                      {emp.first_name} {emp.last_name}
                    </span>
                    {p?.active_app && st!=="offline" && (
                      <span style={{fontSize:10,color:T.dim,maxWidth:80,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        {p.active_app.split(" — ")[0]}
                      </span>
                    )}
                    {p?.today_active_seconds > 0 && (
                      <span style={{fontSize:10,fontFamily:"JetBrains Mono,monospace",color:T.cyan}}>
                        {fmtHM(p.today_active_seconds)}
                      </span>
                    )}
                  </div>
                );
              })}
              {employees.length === 0 && (
                <div style={{fontSize:12,color:T.dim,textAlign:"center"}}>No tracked users yet</div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ADD USER MODAL (lightweight — no salary/leave)
// ══════════════════════════════════════════════════════════════
function AddUserModal({departments, onClose, onSaved, flash}) {
  const {T} = useTheme();
  const inp = useInp();
  const lbl = useLbl();
  const [form, setForm] = useState({
    first_name:"", last_name:"", email:"", phone:"",
    department_id:"", designation_id:"", employee_code:"",
  });
  const [saving, setSaving] = useState(false);
  const f = (k) => (e) => setForm(p=>({...p,[k]:e.target.value}));

  const submit = async () => {
    if(!form.first_name||!form.email){ flash("❌ Name and email are required"); return; }
    setSaving(true);
    try {
      // Create employee record (minimal) — no salary/leave assignment
      await axios.post(`${API}/employees/`, {
        ...form,
        department_id:  form.department_id  ? parseInt(form.department_id)  : null,
        designation_id: form.designation_id ? parseInt(form.designation_id) : null,
        status: "Active",
        date_of_joining: new Date().toISOString().slice(0,10),
        // No salary structure, no leave policy — just tracking
        _skip_salary:   true,
        _skip_leave:    true,
        _source:        "agent_module",
      }, ax());
      flash("✅ User added — share the agent installer with them");
      onSaved();
      onClose();
    } catch(e) { flash("❌ " + (e.response?.data?.detail || "Error adding user")); }
    finally { setSaving(false); }
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:9998,
      display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"Outfit,sans-serif"}}
      onClick={onClose}>
      <div style={{background:T.card,border:`1px solid ${T.cyan}33`,
        borderRadius:14,width:500,overflow:"hidden"}} onClick={e=>e.stopPropagation()}>
        <div style={{padding:"18px 24px",borderBottom:`1px solid ${T.border}`,
          background:`${T.cyan}0A`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <h3 style={{margin:0,fontSize:16,fontWeight:700,color:T.text}}>Add Tracked User</h3>
            <div style={{fontSize:12,color:T.muted,marginTop:3}}>
              Lightweight onboarding — tracking only, no payroll or leave setup
            </div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",color:T.muted,fontSize:18}}>✕</button>
        </div>
        <div style={{padding:24,display:"flex",flexDirection:"column",gap:14}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <div>
              <label style={lbl}>First Name *</label>
              <input value={form.first_name} onChange={f("first_name")} placeholder="First name" style={inp}/>
            </div>
            <div>
              <label style={lbl}>Last Name</label>
              <input value={form.last_name} onChange={f("last_name")} placeholder="Last name" style={inp}/>
            </div>
          </div>
          <div>
            <label style={lbl}>Work Email *</label>
            <input value={form.email} onChange={f("email")} placeholder="work@company.com" style={inp} type="email"/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <div>
              <label style={lbl}>Phone</label>
              <input value={form.phone} onChange={f("phone")} placeholder="Optional" style={inp}/>
            </div>
            <div>
              <label style={lbl}>Employee Code</label>
              <input value={form.employee_code} onChange={f("employee_code")} placeholder="Auto-generated if blank" style={inp}/>
            </div>
          </div>
          <div>
            <label style={lbl}>Department</label>
            <select value={form.department_id} onChange={f("department_id")} style={inp}>
              <option value="">Select department (optional)</option>
              {departments.map(d=><option key={d.id} value={d.id} style={{background:T.opt_bg}}>{d.name}</option>)}
            </select>
          </div>

          <div style={{background:`${T.cyan}0D`,border:`1px solid ${T.cyan}1F`,
            borderRadius:8,padding:"12px 14px",fontSize:12.5,color:T.muted,lineHeight:1.8}}>
            📩 After adding, share the <strong style={{color:T.cyan}}>HiralityAgent.exe</strong> (Windows) or
            <strong style={{color:T.cyan}}> HiralityAgent.app</strong> (macOS) with this user.
            They sign in with <strong style={{color:T.text}}>{form.email||"their email"}</strong> and tracking starts automatically.
          </div>

          <div style={{display:"flex",gap:10,justifyContent:"flex-end",paddingTop:8,borderTop:`1px solid ${T.border}`}}>
            <Btn onClick={onClose} variant="ghost">Cancel</Btn>
            <Btn onClick={submit} disabled={saving}>
              {saving?"Adding…":<><Plus size={13}/>Add & Track</>}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// TRACKERS LIST VIEW
// ══════════════════════════════════════════════════════════════
function TrackersView({employees, presence, weekly, departments, onSelectEmp, onRefresh, flash}) {
  const {T} = useTheme();
  const inp = useInp();
  const STATUS_CFG = useStatusCfg();
  const [search, setSearch]   = useState("");
  const [filter, setFilter]   = useState("all"); // all|active|idle|offline
  const [showAdd, setShowAdd] = useState(false);

  const filtered = useMemo(() => employees.filter(e => {
    const matchS = !search || `${e.first_name} ${e.last_name} ${e.email||""} ${e.employee_code||""}`.toLowerCase().includes(search.toLowerCase());
    const st = presence[e.id]?.status || "offline";
    const matchF = filter==="all" || st===filter;
    return matchS && matchF;
  }), [employees, presence, search, filter]);

  const counts = useMemo(() => ({
    all:     employees.length,
    active:  employees.filter(e=>(presence[e.id]?.status||"offline")==="active").length,
    idle:    employees.filter(e=>(presence[e.id]?.status||"offline")==="idle").length,
    offline: employees.filter(e=>!(presence[e.id]?.status)||presence[e.id]?.status==="offline").length,
  }), [employees, presence]);

  return (
    <div>
      {/* Top bar */}
      <div style={{display:"flex",gap:10,marginBottom:16,alignItems:"center",flexWrap:"wrap"}}>
        <div style={{position:"relative",flex:1,minWidth:200}}>
          <Search size={13} style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:T.dim}}/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search users…"
            style={{...inp,paddingLeft:32}}/>
        </div>
        {/* Filter tabs */}
        <div style={{display:"flex",gap:4}}>
          {["all","active","idle","offline"].map(s=>(
            <button key={s} onClick={()=>setFilter(s)} style={{
              padding:"6px 14px",borderRadius:8,fontSize:12,fontWeight:700,
              cursor:"pointer",fontFamily:"Outfit,sans-serif",border:"none",
              background:filter===s?`linear-gradient(135deg,${T.cyan},${T.purple})`:T.toggle_bg,
              color:filter===s?"#fff":T.muted,
            }}>
              {s.charAt(0).toUpperCase()+s.slice(1)} <span style={{fontFamily:"JetBrains Mono,monospace",fontSize:10}}>({counts[s]})</span>
            </button>
          ))}
        </div>
        <Btn onClick={()=>setShowAdd(true)}><Plus size={13}/>Add User</Btn>
        <Btn onClick={onRefresh} variant="ghost"><RefreshCw size={13}/></Btn>
      </div>

      {/* Table */}
      <Card>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
          <thead>
            <tr style={{borderBottom:`1px solid ${T.border}`}}>
              {["User","Status","Today","This Week","Last Seen","Current App","Actions"].map(h=>(
                <th key={h} style={{padding:"11px 16px",textAlign:"left",fontSize:10,
                  color:T.dim,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.07em"}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((emp,i)=>{
              const p  = presence[emp.id];
              const st = p?.status || "offline";
              const sc = STATUS_CFG[st];
              return (
                <tr key={emp.id} style={{borderBottom:`1px solid ${T.border}`,
                  background:i%2===0?"transparent":T.card2,
                  cursor:"pointer",transition:"background 0.15s"}}
                  onMouseEnter={e=>e.currentTarget.style.background=T.sidebar_hover}
                  onMouseLeave={e=>e.currentTarget.style.background=i%2===0?"transparent":T.card2}
                  onClick={()=>onSelectEmp(emp)}>
                  <td style={{padding:"12px 16px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{width:34,height:34,borderRadius:9,flexShrink:0,position:"relative",
                        background:`linear-gradient(135deg,${T.cyan},${T.purple})`,
                        display:"flex",alignItems:"center",justifyContent:"center",
                        fontSize:11,fontWeight:700,color:"#fff"}}>
                        {initials(emp.first_name,emp.last_name)}
                        {st==="active" && (
                          <div style={{position:"absolute",bottom:-1,right:-1,width:9,height:9,
                            borderRadius:"50%",background:T.green,border:`2px solid ${T.card}`,
                            boxShadow:`0 0 5px ${T.green}`}}/>
                        )}
                      </div>
                      <div>
                        <div style={{fontWeight:600,color:T.text}}>{emp.first_name} {emp.last_name}</div>
                        <div style={{fontSize:11,color:T.dim}}>{emp.email||emp.employee_code||"—"}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{padding:"12px 16px"}}>
                    <div style={{display:"inline-flex",alignItems:"center",gap:5,
                      padding:"3px 9px",borderRadius:20,
                      background:`${sc.color}18`,border:`1px solid ${sc.color}35`,
                      color:sc.color,fontSize:11,fontWeight:700}}>
                      {sc.icon} {sc.label}
                    </div>
                  </td>
                  <td style={{padding:"12px 16px",fontFamily:"JetBrains Mono,monospace",
                    color:p?.today_active_seconds>0?T.cyan:T.dim,fontWeight:700}}>
                    {p?.today_active_seconds > 0 ? fmtHM(p.today_active_seconds) : "—"}
                  </td>
                  <td style={{padding:"12px 16px",fontFamily:"JetBrains Mono,monospace",
                    color:weekly[emp.id]>0?T.purple:T.dim,fontWeight:700}}>
                    {weekly[emp.id] > 0 ? fmtHM(weekly[emp.id]) : "—"}
                  </td>
                  <td style={{padding:"12px 16px",color:T.muted,fontSize:12}}>
                    {p?.last_heartbeat ? fmtDate(p.last_heartbeat) : "—"}
                  </td>
                  <td style={{padding:"12px 16px"}}>
                    {p?.active_app && st!=="offline" ? (
                      <div style={{display:"flex",alignItems:"center",gap:6}}>
                        <span style={{fontSize:14}}>{appIcon(p.active_app)}</span>
                        <span style={{fontSize:11,color:T.muted,maxWidth:120,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          {p.active_app.split(" — ")[0]}
                        </span>
                        {isProductive(p.active_app) && (
                          <span style={{fontSize:9,padding:"1px 5px",borderRadius:6,
                            background:`${T.green}18`,color:T.green,fontWeight:700}}>PRO</span>
                        )}
                      </div>
                    ) : <span style={{color:T.dim,fontSize:12}}>—</span>}
                  </td>
                  <td style={{padding:"12px 16px"}}>
                    <Btn onClick={e=>{e.stopPropagation();onSelectEmp(emp);}} variant="ghost" size="sm">
                      <Eye size={11}/>View
                    </Btn>
                  </td>
                </tr>
              );
            })}
            {filtered.length===0 && (
              <tr><td colSpan={7} style={{padding:"40px",textAlign:"center",color:T.dim,fontSize:13}}>
                {search ? "No users match your search" : "No tracked users yet — click Add User to get started"}
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {showAdd && (
        <AddUserModal
          departments={departments}
          onClose={()=>setShowAdd(false)}
          onSaved={onRefresh}
          flash={flash}/>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// INDIVIDUAL DETAIL VIEW
// ══════════════════════════════════════════════════════════════
function DetailView({emp, presence, onBack}) {
  const {T, theme} = useTheme();
  const inp = useInp();
  const STATUS_CFG = useStatusCfg();
  const [period, setPeriod]   = useState("today");  // today|week|month|custom
  const [fromDate,setFromDate]= useState(new Date().toISOString().slice(0,10));
  const [toDate,  setToDate]  = useState(new Date().toISOString().slice(0,10));
  const [summary, setSummary] = useState([]);  // [{date, active_seconds, last_seen}]
  const [apps,    setApps]    = useState([]);  // [{app, seconds, pct}] for day view
  const [loading, setLoading] = useState(false);

  const p  = presence[emp.id] || {};
  const st = p.status || "offline";

  const getRange = useCallback(() => {
    const today  = new Date();
    const todayS = today.toISOString().slice(0,10);
    if(period==="today")  return [todayS, todayS];
    if(period==="week") {
      const mon = new Date(today); mon.setDate(today.getDate()-today.getDay()+1);
      return [mon.toISOString().slice(0,10), todayS];
    }
    if(period==="month") {
      return [`${todayS.slice(0,7)}-01`, todayS];
    }
    return [fromDate, toDate];
  }, [period, fromDate, toDate]);

  const load = useCallback(async () => {
    setLoading(true);
    const [from, to] = getRange();
    try {
      const [sumRes, appRes] = await Promise.all([
        fetch(`${API}/presence/daily-report?employee_id=${emp.id}&from_date=${from}&to_date=${to}`,
          {headers:(ax()).headers}),
        period==="today"
          ? fetch(`${API}/presence/app-breakdown?employee_id=${emp.id}&date=${from}`,{headers:(ax()).headers})
          : Promise.resolve({ok:true,json:()=>[]})
      ]);
      if(sumRes.ok) setSummary(await sumRes.json());
      if(appRes.ok) setApps(await appRes.json());
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [emp.id, getRange, period]);

  useEffect(() => { load(); }, [load]);

  const totalSecs   = summary.reduce((a,d)=>a+(d.active_seconds||0),0);
  const activeDays  = summary.filter(d=>d.active_seconds>0).length;
  const avgSecs     = activeDays ? Math.round(totalSecs/activeDays) : 0;

  // Productivity score from apps
  const productiveApps = apps.filter(a=>isProductive(a.app));
  const productivePct  = apps.length
    ? Math.round(productiveApps.reduce((a,x)=>a+x.seconds,0)/Math.max(totalSecs,1)*100) : 0;

  // Chart data
  const chartData = summary.map(d=>({
    date: d.date?.slice(5),  // MM-DD
    hours: parseFloat(fmtH(d.active_seconds||0)),
  }));

  // Pie data for apps
  const pieData = apps.slice(0,6).map((a,i)=>({
    name: a.app?.split(" — ")[0]||"Unknown",
    value: Math.round(a.seconds/60),
    pct: a.pct,
  }));

  const PERIOD_TABS = [
    {key:"today", label:"Today"},
    {key:"week",  label:"This Week"},
    {key:"month", label:"This Month"},
    {key:"custom",label:"Custom"},
  ];

  return (
    <div>
      {/* Header */}
      <div style={{display:"flex",gap:16,alignItems:"flex-start",marginBottom:22}}>
        <button onClick={onBack} style={{background:"none",border:`1px solid ${T.border}`,
          borderRadius:8,padding:"7px 14px",cursor:"pointer",color:T.muted,
          fontFamily:"Outfit,sans-serif",fontSize:13,display:"flex",alignItems:"center",gap:6}}>
          <ChevronLeft size={14}/> Back
        </button>
        <div style={{display:"flex",gap:16,alignItems:"center",flex:1}}>
          <div style={{width:52,height:52,borderRadius:12,
            background:`linear-gradient(135deg,${T.cyan},${T.purple})`,
            display:"flex",alignItems:"center",justifyContent:"center",
            fontSize:16,fontWeight:700,color:"#fff",flexShrink:0,position:"relative"}}>
            {initials(emp.first_name,emp.last_name)}
            {st==="active" && (
              <div style={{position:"absolute",bottom:-2,right:-2,width:12,height:12,
                borderRadius:"50%",background:T.green,border:`2px solid ${T.card}`,
                boxShadow:`0 0 6px ${T.green}`}}/>
            )}
          </div>
          <div>
            <h2 style={{margin:0,fontSize:20,fontWeight:800,color:T.text}}>
              {emp.first_name} {emp.last_name}
            </h2>
            <div style={{fontSize:13,color:T.muted,marginTop:2}}>
              {emp.email||"—"} · {emp.department?.name||emp.employee_code||"No department"}
            </div>
          </div>
          {/* Live status */}
          <div style={{marginLeft:"auto",textAlign:"right"}}>
            <div style={{display:"inline-flex",alignItems:"center",gap:6,
              padding:"6px 14px",borderRadius:20,
              background:`${STATUS_CFG[st].color}18`,
              border:`1px solid ${STATUS_CFG[st].color}35`,
              color:STATUS_CFG[st].color,fontSize:12,fontWeight:700}}>
              <StatusDot status={st}/> {STATUS_CFG[st].label}
            </div>
            {p.active_app && st!=="offline" && (
              <div style={{fontSize:11,color:T.dim,marginTop:4}}>
                {appIcon(p.active_app)} {p.active_app.split(" — ")[0]}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Period selector */}
      <div style={{display:"flex",gap:6,marginBottom:18,alignItems:"center",flexWrap:"wrap"}}>
        {PERIOD_TABS.map(t=>(
          <button key={t.key} onClick={()=>setPeriod(t.key)} style={{
            padding:"7px 16px",borderRadius:8,border:"none",cursor:"pointer",
            fontFamily:"Outfit,sans-serif",fontSize:12,fontWeight:700,
            background:period===t.key?`linear-gradient(135deg,${T.cyan},${T.purple})`:T.toggle_bg,
            color:period===t.key?"#fff":T.muted,
          }}>{t.label}</button>
        ))}
        {period==="custom" && (
          <>
            <input type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)}
              style={{...inp,width:150,colorScheme:theme}}/>
            <span style={{color:T.muted,fontSize:12}}>to</span>
            <input type="date" value={toDate} min={fromDate} onChange={e=>setToDate(e.target.value)}
              style={{...inp,width:150,colorScheme:theme}}/>
            <Btn onClick={load} size="sm">Apply</Btn>
          </>
        )}
        <Btn onClick={load} variant="ghost" size="sm" style={{marginLeft:"auto"}}>
          <RefreshCw size={11}/>Refresh
        </Btn>
      </div>

      {/* Summary stats */}
      <div style={{display:"flex",gap:12,marginBottom:18,flexWrap:"wrap"}}>
        <StatCard label="Total Time"     value={fmtHM(totalSecs)} color="cyan"   icon={<Clock size={16}/>}     sub="Tracked active time"/>
        <StatCard label="Active Days"    value={activeDays}        color="green"  icon={<CheckCircle size={16}/>} sub={`of ${summary.length} day${summary.length!==1?"s":""} in range`}/>
        <StatCard label="Avg/Day"        value={fmtHM(avgSecs)}   color="purple" icon={<TrendingUp size={16}/>} sub="On days with activity"/>
        <StatCard label="Productivity"   value={fmtPct(productivePct)} color={productivePct>60?"green":productivePct>30?"amber":"red"}
          icon={<Zap size={16}/>} sub="Time on productive apps"/>
        <StatCard label="Apps Used"      value={apps.length||"—"} color="amber"  icon={<Monitor size={16}/>}  sub={period==="today"?"Unique apps today":"Today's data"}/>
      </div>

      {/* Charts */}
      {period !== "today" ? (
        <div style={{display:"grid",gridTemplateColumns:"1fr",gap:16,marginBottom:16}}>
          <Card style={{padding:"18px 20px"}}>
            <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:14}}>
              Daily Active Hours
            </div>
            {chartData.length>0 ? (
              <div style={{display:"flex",flexDirection:"column",gap:7,maxHeight:220,overflowY:"auto"}}>
                {chartData.map((d,i)=>{
                  const maxH = Math.max(...chartData.map(x=>x.hours),8);
                  const pct  = Math.round((d.hours/maxH)*100);
                  const barColor = d.hours>=6?T.green:d.hours>=3?T.cyan:d.hours>0?T.amber:T.dim;
                  return (
                    <div key={i} style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{width:44,fontSize:10,color:T.muted,textAlign:"right",flexShrink:0}}>{d.date}</div>
                      <div style={{flex:1,height:18,background:T.toggle_bg,borderRadius:4,overflow:"hidden"}}>
                        <div style={{width:`${pct}%`,height:"100%",background:barColor,borderRadius:4,
                          transition:"width 0.5s",minWidth:d.hours>0?3:0}}/>
                      </div>
                      <div style={{width:36,fontSize:11,fontFamily:"JetBrains Mono,monospace",
                        color:barColor,fontWeight:700,flexShrink:0}}>
                        {d.hours>0?`${d.hours}h`:"—"}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{height:140,display:"flex",alignItems:"center",justifyContent:"center",color:T.dim,fontSize:13}}>
                {loading?"Loading…":"No data for this period"}
              </div>
            )}
          </Card>
        </div>
      ) : (
        /* Today: App breakdown */
        apps.length > 0 && (
          <div style={{display:"grid",gridTemplateColumns:"1fr",gap:16,marginBottom:16}}>
            <Card style={{padding:"18px 20px"}}>
              <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:4}}>App Usage Today</div>
              <div style={{fontSize:11,color:T.dim,marginBottom:14}}>Time distribution across applications</div>
              {/* Stacked bar showing app time proportions */}
              {pieData.length>0 && (
                <div style={{height:20,borderRadius:6,overflow:"hidden",display:"flex",marginBottom:14}}>
                  {pieData.map((d,i)=>(
                    <div key={i} title={`${d.name}: ${d.pct}%`}
                      style={{width:`${d.pct}%`,background:COLORS[i%COLORS.length],
                        transition:"width 0.5s",minWidth:d.pct>2?undefined:0}}/>
                  ))}
                </div>
              )}
            </Card>
            <Card style={{padding:"18px 20px",overflowY:"auto",maxHeight:250}}>
              <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:14}}>All Apps — Today</div>
              {apps.map((a,i)=>(
                <div key={a.app} style={{marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                    <div style={{display:"flex",alignItems:"center",gap:7}}>
                      <span style={{fontSize:16}}>{appIcon(a.app)}</span>
                      <span style={{fontSize:12,color:T.muted}}>{a.app?.split(" — ")[0]||"Unknown"}</span>
                      {isProductive(a.app||"")&&<span style={{fontSize:9,padding:"1px 5px",borderRadius:6,
                        background:`${T.green}15`,color:T.green,fontWeight:700}}>PRO</span>}
                    </div>
                    <div style={{textAlign:"right"}}>
                      <span style={{fontSize:12,fontWeight:700,color:COLORS[i%COLORS.length],fontFamily:"JetBrains Mono,monospace"}}>
                        {fmtHM(a.seconds)}
                      </span>
                      <span style={{fontSize:10,color:T.dim,marginLeft:5}}>{a.pct}%</span>
                    </div>
                  </div>
                  <div style={{height:4,background:T.toggle_bg,borderRadius:2}}>
                    <div style={{height:"100%",borderRadius:2,width:`${a.pct}%`,
                      background:COLORS[i%COLORS.length],transition:"width 0.5s"}}/>
                  </div>
                </div>
              ))}
            </Card>
          </div>
        )
      )}

      {/* Daily breakdown table (for week/month/custom) */}
      {period !== "today" && summary.length > 0 && (
        <Card>
          <div style={{padding:"12px 18px",borderBottom:`1px solid ${T.border}`,fontSize:13,fontWeight:700,color:T.text}}>
            Daily Breakdown
          </div>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
            <thead>
              <tr style={{borderBottom:`1px solid ${T.border}`}}>
                {["Date","Day","Active Time","Status","Last Seen"].map(h=>(
                  <th key={h} style={{padding:"9px 16px",textAlign:"left",fontSize:10,
                    color:T.dim,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.07em"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {summary.map((d,i)=>{
                const secs = d.active_seconds||0;
                const day  = new Date(d.date).toLocaleDateString("en-IN",{weekday:"short"});
                const color= secs>=21600?T.green:secs>=14400?T.cyan:secs>0?T.amber:T.dim;
                return (
                  <tr key={d.date} style={{borderBottom:`1px solid ${T.border}`,
                    background:i%2===0?"transparent":T.card2}}>
                    <td style={{padding:"9px 16px",color:T.muted,fontSize:12}}>
                      {new Date(d.date).toLocaleDateString("en-IN",{day:"2-digit",month:"short"})}
                    </td>
                    <td style={{padding:"9px 16px",color:T.dim,fontSize:12}}>{day}</td>
                    <td style={{padding:"9px 16px",fontFamily:"JetBrains Mono,monospace",fontWeight:700,color}}>
                      {secs>0?fmtHM(secs):"—"}
                    </td>
                    <td style={{padding:"9px 16px"}}>
                      <span style={{fontSize:11,padding:"2px 8px",borderRadius:20,
                        background:`${color}15`,color,fontWeight:700}}>
                        {secs>=21600?"Full Day":secs>=14400?"Good":secs>0?"Partial":"No Activity"}
                      </span>
                    </td>
                    <td style={{padding:"9px 16px",color:T.muted,fontSize:12}}>
                      {d.last_seen?new Date(d.last_seen).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"}):"—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// REPORTS VIEW
// ══════════════════════════════════════════════════════════════
function ReportsView({employees, flash}) {
  const {T, theme} = useTheme();
  const inp = useInp();
  const lbl = useLbl();
  const today = new Date().toISOString().slice(0,10);
  const firstOfMonth = `${today.slice(0,7)}-01`;
  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate,   setToDate]   = useState(today);
  const [data,     setData]     = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [generated,setGenerated]= useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const rows = await Promise.all(employees.map(async (emp) => {
        try {
          const r = await fetch(
            `${API}/presence/daily-report?employee_id=${emp.id}&from_date=${fromDate}&to_date=${toDate}`,
            {headers:(ax()).headers}
          );
          const days = r.ok ? await r.json() : [];
          const totalSecs = days.reduce((a,d)=>a+(d.active_seconds||0),0);
          const activeDays= days.filter(d=>d.active_seconds>0).length;
          const totalDays = days.length;
          return {
            employee_code:  emp.employee_code||"—",
            employee_name:  `${emp.first_name} ${emp.last_name}`,
            department:     emp.department?.name||"—",
            total_days:     totalDays,
            active_days:    activeDays,
            absent_days:    totalDays - activeDays,
            total_seconds:  totalSecs,
            avg_seconds:    activeDays ? Math.round(totalSecs/activeDays) : 0,
            attendance_pct: totalDays ? Math.round(activeDays/totalDays*100) : 0,
          };
        } catch { return null; }
      }));
      setData(rows.filter(Boolean).sort((a,b)=>b.total_seconds-a.total_seconds));
      setGenerated(true);
    } catch(e) { flash("❌ Error generating report"); }
    finally { setLoading(false); }
  };

  const exportCSV = () => {
    const headers = ["Emp Code","Name","Department","Total Days","Active Days",
                     "Absent Days","Total Hours","Avg Hours/Day","Attendance %"];
    const rows = data.map(r=>[r.employee_code,r.employee_name,r.department,
      r.total_days,r.active_days,r.absent_days,
      fmtH(r.total_seconds),fmtH(r.avg_seconds),`${r.attendance_pct}%`]);
    const csv = [headers,...rows].map(r=>r.join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv],{type:"text/csv"}));
    a.download = `TimeTracker_Report_${fromDate}_to_${toDate}.csv`;
    a.click(); URL.revokeObjectURL(a.href);
  };

  return (
    <div>
      {/* Controls */}
      <Card style={{padding:"20px 24px",marginBottom:18}}>
        <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:14}}>
          📊 Generate Attendance & Productivity Report
        </div>
        <div style={{display:"flex",gap:12,alignItems:"flex-end",flexWrap:"wrap"}}>
          <div>
            <label style={lbl}>From Date</label>
            <input type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)}
              style={{...inp,width:160,colorScheme:theme}}/>
          </div>
          <div>
            <label style={lbl}>To Date</label>
            <input type="date" value={toDate} min={fromDate} onChange={e=>setToDate(e.target.value)}
              style={{...inp,width:160,colorScheme:theme}}/>
          </div>
          <Btn onClick={generate} disabled={loading}>
            {loading?"Generating…":<><BarChart2 size={13}/>Generate</>}
          </Btn>
          {generated && (
            <Btn onClick={exportCSV} variant="green">
              <Download size={13}/>Export CSV
            </Btn>
          )}
        </div>
      </Card>

      {/* Summary */}
      {generated && data.length>0 && (
        <>
          <div style={{display:"flex",gap:12,marginBottom:16,flexWrap:"wrap"}}>
            <StatCard label="Users Tracked"  value={data.length}
              color="cyan" icon={<Users size={16}/>}/>
            <StatCard label="Team Total Hours" value={fmtH(data.reduce((a,r)=>a+r.total_seconds,0))}
              color="green" icon={<Clock size={16}/>} sub={`${fromDate} to ${toDate}`}/>
            <StatCard label="Avg Attendance"
              value={fmtPct(data.reduce((a,r)=>a+r.attendance_pct,0)/data.length)}
              color="purple" icon={<Activity size={16}/>}/>
            <StatCard label="Top Performer"
              value={data[0]?.employee_name?.split(" ")[0]||"—"}
              color="amber" icon={<TrendingUp size={16}/>}
              sub={fmtHM(data[0]?.total_seconds||0)}/>
          </div>

          <Card>
            <div style={{padding:"12px 18px",borderBottom:`1px solid ${T.border}`,
              display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:13,fontWeight:700,color:T.text}}>
                Individual Report — {fromDate} to {toDate}
              </span>
              <span style={{fontSize:11,color:T.dim}}>{data.length} users</span>
            </div>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                <thead>
                  <tr style={{borderBottom:`1px solid ${T.border}`}}>
                    {["#","Name","Department","Active Days","Total Hours","Avg/Day","Attendance","Standing"].map(h=>(
                      <th key={h} style={{padding:"9px 14px",textAlign:"left",fontSize:10,
                        color:T.dim,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.07em"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((r,i)=>{
                    const pct = r.attendance_pct;
                    const [standing,sColor] = pct>=90?["Excellent",T.green]:pct>=70?["Good",T.cyan]:pct>=50?["Average",T.amber]:["Low",T.red];
                    return (
                      <tr key={r.employee_code} style={{borderBottom:`1px solid ${T.border}`,
                        background:i%2===0?"transparent":T.card2}}>
                        <td style={{padding:"9px 14px",color:T.dim,fontSize:12,fontFamily:"JetBrains Mono,monospace"}}>{i+1}</td>
                        <td style={{padding:"9px 14px"}}>
                          <div style={{fontWeight:600,color:T.text}}>{r.employee_name}</div>
                          <div style={{fontSize:11,color:T.dim}}>{r.employee_code}</div>
                        </td>
                        <td style={{padding:"9px 14px",color:T.muted,fontSize:12}}>{r.department}</td>
                        <td style={{padding:"9px 14px",fontFamily:"JetBrains Mono,monospace",color:T.cyan,fontWeight:700}}>
                          {r.active_days}/{r.total_days}
                        </td>
                        <td style={{padding:"9px 14px",fontFamily:"JetBrains Mono,monospace",fontWeight:700,
                          color:r.total_seconds>=172800?T.green:r.total_seconds>=86400?T.cyan:T.amber}}>
                          {fmtH(r.total_seconds)}
                        </td>
                        <td style={{padding:"9px 14px",color:T.muted,fontSize:12,fontFamily:"JetBrains Mono,monospace"}}>
                          {fmtH(r.avg_seconds)}
                        </td>
                        <td style={{padding:"9px 14px"}}>
                          <div style={{display:"flex",alignItems:"center",gap:7}}>
                            <div style={{width:60,height:5,background:T.toggle_bg,borderRadius:3}}>
                              <div style={{width:`${pct}%`,height:"100%",borderRadius:3,
                                background:pct>=70?T.green:pct>=40?T.amber:T.red}}/>
                            </div>
                            <span style={{fontSize:11,fontFamily:"JetBrains Mono,monospace",color:T.muted}}>{pct}%</span>
                          </div>
                        </td>
                        <td style={{padding:"9px 14px"}}>
                          <span style={{fontSize:11,padding:"2px 8px",borderRadius:20,fontWeight:700,
                            background:`${sColor}15`,color:sColor}}>{standing}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {generated && data.length===0 && (
        <Card style={{padding:40,textAlign:"center"}}>
          <AlertCircle size={36} style={{color:T.dim,marginBottom:12}}/>
          <div style={{fontSize:14,color:T.dim}}>No tracking data found for this period</div>
          <div style={{fontSize:12,color:T.dim,marginTop:6}}>Make sure the desktop agent is installed and users have logged in</div>
        </Card>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN MODULE
// ══════════════════════════════════════════════════════════════
function DesktopAgentModule() {
  const {T} = useTheme();
  const [view,       setView]       = useState("dashboard");
  const [selEmp,     setSelEmp]     = useState(null);
  const [employees,  setEmployees]  = useState([]);
  const [departments,setDepartments]= useState([]);
  const [presence,   setPresence]   = useState({});  // {employee_id: {status, active_app, today_active_seconds, last_heartbeat}}
  const [weekly,     setWeekly]     = useState({});  // {employee_id: total_active_seconds_this_week}
  const [loading,    setLoading]    = useState(true);
  const [toast,      setToast]      = useState("");
  const [lastRefresh,setLastRefresh]= useState(null);

  const flash = (msg) => { setToast(msg); setTimeout(()=>setToast(""),4000); };

  const loadEmployees = useCallback(async () => {
    try {
      const [eRes, dRes] = await Promise.all([
        axios.get(`${APIConstant.employees}?status=Active`),
        axios.get(APIConstant.departments),
      ]);
      setEmployees(eRes.data || []);
      setDepartments(dRes.data || []);
    } catch(e) { console.error(e); }
  }, []);

  const loadPresence = useCallback(async () => {
    try {
      const res = await fetch(`${API}/presence/team`, ax()); // uses platform_token via ax()
      if(res.ok) {
        const data = await res.json();
        if(data.employees) {
          const map = {};
          data.employees.forEach(e => { map[e.employee_id] = e; });
          setPresence(map);
        }
      }
    } catch(e) { console.error(e); }
    setLastRefresh(new Date());
  }, []);

  const loadWeekly = useCallback(async () => {
    try {
      const today  = new Date();
      const monday = new Date(today);
      const dow    = (today.getDay() + 6) % 7; // 0=Mon..6=Sun
      monday.setDate(today.getDate() - dow);
      const fd = monday.toISOString().slice(0,10);
      const td = today.toISOString().slice(0,10);
      const res = await fetch(`${API}/presence/daily-report?from_date=${fd}&to_date=${td}`, ax());
      if(res.ok) {
        const rows = await res.json();
        const totals = {};
        (rows||[]).forEach(r => { totals[r.employee_id] = (totals[r.employee_id]||0) + (r.active_seconds||0); });
        setWeekly(totals);
      }
    } catch(e) { console.error(e); }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadEmployees(), loadPresence(), loadWeekly()]);
    setLoading(false);
  }, [loadEmployees, loadPresence, loadWeekly]);

  useEffect(() => { refresh(); }, [refresh]);

  // Presence loads on mount (via refresh) and on manual Refresh click only.
  // No auto-poll: agent pushes heartbeats to backend every 60s — data is
  // always fresh. Polling from the browser caused 401s and unnecessary load.

  const handleSelectEmp = (emp) => { setSelEmp(emp); setView("detail"); };
  const handleBack      = ()    => { setView("trackers"); setSelEmp(null); };

  const TABS = [
    {key:"dashboard", icon:<Activity size={15}/>,  label:"Dashboard"},
    {key:"trackers",  icon:<Users size={15}/>,     label:"Trackers"},
    {key:"reports",   icon:<FileText size={15}/>,  label:"Reports"},
  ];

  return (
    <div style={{fontFamily:"Outfit,sans-serif",color:T.text,background:T.bg,
      minHeight:"100%",transition:"background 0.25s,color 0.25s"}}>
      {/* Page header */}
      <div style={{marginBottom:20,display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
        <div>
          <div style={{fontSize:11,color:T.cyan,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:4}}>
            TIME TRACKING
          </div>
          <h2 style={{fontSize:22,fontWeight:800,margin:"0 0 4px",color:T.text}}>Desktop Agent</h2>
          <p style={{fontSize:13,color:T.muted,margin:0}}>
            Live activity monitoring, app usage analytics and productivity reports
          </p>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {lastRefresh && (
            <span style={{fontSize:11,color:T.dim}}>
              Updated {lastRefresh.toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}
            </span>
          )}
          <Btn onClick={refresh} variant="ghost" size="sm" disabled={loading}>
            <RefreshCw size={12} style={{animation:loading?"spin 1s linear infinite":undefined}}/> Refresh
          </Btn>
          <a href="/agent/HiralityAgent.exe" download style={{textDecoration:"none"}}>
            <Btn variant="ghost" size="sm"><Download size={12}/> Get Agent</Btn>
          </a>
        </div>
      </div>

      {/* Tabs */}
      {view !== "detail" && (
        <div style={{display:"flex",gap:4,borderBottom:`1px solid ${T.border}`,marginBottom:20}}>
          {TABS.map(t=>(
            <button key={t.key} onClick={()=>setView(t.key)} style={{
              padding:"10px 20px",borderRadius:"8px 8px 0 0",
              border:`1px solid ${view===t.key?T.border:"transparent"}`,
              borderBottom:"none",
              background:view===t.key?T.card:"transparent",
              color:view===t.key?T.cyan:T.muted,
              cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13,fontWeight:700,
              marginBottom:view===t.key?-1:0,
              display:"flex",alignItems:"center",gap:7,
            }}>{t.icon}{t.label}</button>
          ))}
          {/* Live indicator */}
          <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:6,
            padding:"0 14px",fontSize:12,color:T.green}}>
            <span style={{width:7,height:7,borderRadius:"50%",background:T.green,
              display:"inline-block",boxShadow:`0 0 6px ${T.green}`,animation:"pulse 1.5s infinite"}}/>
            {Object.values(presence).filter(p=>p.status==="active").length} active now
          </div>
        </div>
      )}

      {/* Views */}
      {loading && view==="dashboard" ? (
        <div style={{padding:60,textAlign:"center",color:T.muted,fontSize:13}}>Loading…</div>
      ) : view==="dashboard" ? (
        <DashboardView employees={employees} presence={presence}/>
      ) : view==="trackers" ? (
        <TrackersView
          employees={employees} presence={presence} weekly={weekly} departments={departments}
          onSelectEmp={handleSelectEmp} onRefresh={refresh} flash={flash}/>
      ) : view==="detail" && selEmp ? (
        <DetailView emp={selEmp} presence={presence} onBack={handleBack}/>
      ) : view==="reports" ? (
        <ReportsView employees={employees} flash={flash}/>
      ) : null}

      {/* Toast */}
      {toast && (
        <div style={{position:"fixed",bottom:28,left:"50%",transform:"translateX(-50%)",
          padding:"10px 22px",borderRadius:10,fontSize:13,fontWeight:600,zIndex:9999,
          background:T.card,
          border:`1px solid ${toast.startsWith("✅")?`${T.green}4D`:`${T.red}4D`}`,
          color:toast.startsWith("✅")?T.green:T.red,
          boxShadow:T.shadow,fontFamily:"Outfit,sans-serif"}}>
          {toast}
        </div>
      )}

      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}} @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

export default DesktopAgentModule;